/**
 * Sticky 2-tier site header, the same as the corporate site's
 * (new_wp / providecars.co.jp): Royal Navy top bar (XL+ only) + frosted white
 * main nav with logo, primary nav and the gold "Auction Sign Up" pill. Mobile
 * breakpoint replaces the top bar with a hamburger drawer.
 *
 * Signed in, the Stock entries open Stock through /stock-handoff so the
 * customer arrives there signed in too (lib/stockSession.ts).
 */
import { Link } from 'react-router-dom';
import { TopBarSignIn, type TopBarUser } from './TopBarSignIn';
import { DesktopMegaItem, type NavItem } from './DesktopMegaItem';
import { MobileNav } from './MobileNav';
import { ChromeLink } from './ChromeLink';
import {
  AUCTION_HREF,
  AUCTION_SIGNUP_HREF,
  BLOG_HREF,
  CONTACT_HREF,
  DEFAULT_NAV,
  STOCK_HREF,
} from './chromeNav';
import { STOCK_HANDOFF_PATH } from '../../lib/stockSession';

interface Props {
  navItems?: NavItem[];
  /** When provided, the right-side widgets switch from "Sign In" to a
   *  signed-in user menu (Dashboard / Account / Sign out). AppShell wires
   *  this from `/me`; PublicShell omits it. */
  user?: TopBarUser;
}

/** Where a "Stock" link goes: the plain site, or (signed in) the hand-off that signs in there too. */
function stockLinkFor(user: TopBarUser | undefined): string {
  if (!user || typeof window === 'undefined') return STOCK_HREF;
  return `${window.location.origin}${STOCK_HANDOFF_PATH}`;
}

const TOP_LINK =
  'text-sm font-medium text-white/70 hover:text-white transition-colors duration-150';

export function Header({ navItems = DEFAULT_NAV, user }: Props = {}): JSX.Element {
  const stockHref = stockLinkFor(user);
  const items = navItems.map((item) =>
    item.path === STOCK_HREF ? { ...item, path: stockHref } : item,
  );

  return (
    <header className="sticky top-0 z-50" role="banner">
      {/* Top bar (XL+) */}
      <div
        className="hidden xl:block border-b border-slate-200/60"
        style={{ backgroundColor: 'rgb(0, 0, 58)' }}
      >
        <div className="max-w-[1760px] mx-auto px-4 sm:px-6 xl:px-12 2xl:px-16">
          <div className="flex items-center justify-between h-9">
            <div className="flex items-center gap-8">
              <a href={AUCTION_HREF} target="_blank" rel="noopener noreferrer" className={TOP_LINK}>
                Auction
              </a>
              <a href={stockHref} target="_blank" rel="noopener noreferrer" className={TOP_LINK}>
                Stock
              </a>
            </div>
            <div className="flex items-center gap-5">
              <ChromeLink to={CONTACT_HREF} className={TOP_LINK}>
                Contact
              </ChromeLink>
              <TopBarSignIn user={user} />
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white/80 backdrop-blur-lg backdrop-saturate-150 border-b border-slate-200/60 shadow-[0_1px_3px_0_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out">
        <div className="max-w-[1760px] mx-auto px-4 sm:px-6 xl:px-12 2xl:px-16">
          <div className="flex items-center justify-between lg:justify-start h-16 lg:h-20">
            {/* Mobile hamburger + drawer (< xl) */}
            <MobileNav navItems={items} user={user} stockHref={stockHref} />

            {/* Logo */}
            <Link
              to="/"
              aria-label="Provide Cars - Home"
              className="flex items-center gap-2.5 shrink-0 group"
            >
              <img
                src="/logos/provide-cars-icon.svg"
                alt=""
                width={40}
                height={40}
                className="size-9 lg:size-10"
                role="presentation"
              />
              <span className="text-lg lg:text-xl font-bold tracking-wide text-foreground">
                PROVIDE CARS
              </span>
            </Link>

            {/* Desktop nav (xl+) */}
            <nav
              aria-label="Main navigation"
              className="hidden xl:flex items-center gap-3 xl:gap-4 2xl:gap-7 ml-4 xl:ml-6 2xl:ml-12"
            >
              {items.map((item) => (
                <DesktopMegaItem
                  key={item.title}
                  title={item.title}
                  path={item.path}
                  subItems={item.children}
                />
              ))}
              <ChromeLink
                to={BLOG_HREF}
                className="text-xs lg:text-sm 2xl:text-base font-medium text-foreground-secondary hover:text-foreground transition-colors duration-150 whitespace-nowrap"
              >
                Blog
              </ChromeLink>
            </nav>

            {/* CTA — pushed to right */}
            <div className="hidden xl:block ml-auto">
              <a
                href={AUCTION_SIGNUP_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 lg:px-5 lg:py-2.5 bg-brand-500 text-brand-950 text-xs lg:text-sm 2xl:text-base font-medium rounded-full whitespace-nowrap shadow-lg shadow-brand-500/30 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-brand-500/40 transition-all duration-300 focus-visible:outline-3 focus-visible:outline-brand-500 focus-visible:outline-offset-2"
              >
                Auction Sign Up
              </a>
            </div>

            {/* Right spacer keeps the mobile logo centered with the hamburger */}
            <div className="w-10 xl:hidden" aria-hidden="true" />
          </div>
        </div>
      </div>
    </header>
  );
}
